import { RegistrationFormSchema } from "../../../apps/gadget/shared/types/RegistrationForm.ts";

const DEFAULT_PHONE_PREFIX = "+44";
const UK_E164_NATIONAL_NUMBER_LENGTH = 10;
const VAT_COUNTRY_CODE_LENGTH = 2;
const VAT_DIGITS_MAX_LENGTH = 12;
const VAT_MAX_LENGTH = VAT_COUNTRY_CODE_LENGTH + VAT_DIGITS_MAX_LENGTH;
const RECAPTCHA_ACTION = "registration_form_submit";
const RECAPTCHA_SCRIPT_SELECTOR = 'script[data-recaptcha-v2="registration-form"]';
const RECAPTCHA_TOKEN_TIMEOUT_MS = 10000;

/**
 * Builds a blank address model.
 * @returns {{ address1: string; address2: string; city: string; zip: string; countryCode: string }} Address model.
 */
const createAddress = () => ({
  address1: "",
  address2: "",
  city: "",
  zip: "",
  countryCode: "GB"
});

/**
 * Builds a new user model.
 * @returns {{ title: "Mr" | "Mrs" | "Ms" | "Miss" | "Dr"; firstName: string; lastName: string; email: string; phone: string }} User model.
 */
const createUser = () => ({
  title: "Mr",
  firstName: "",
  lastName: "",
  email: "",
  phone: DEFAULT_PHONE_PREFIX
});

/**
 * Builds the initial form model.
 * @returns {import("../../../apps/gadget/shared/types/RegistrationForm.ts").RegistrationForm} Form model.
 */
const createInitialForm = () => ({
  company: {
    name: "",
    legalStatus: "SOLE_TRADER",
    vatNumber: "",
    companyRegistrationNumber: "",
    tradeRelationship: "INDEPENDENT",
    faciaGroupId: "",
    faciaId: "",
    user: createUser(),
    location: {
      billingSameAsShipping: true,
      shippingAddress: createAddress(),
      billingAddress: createAddress()
    }
  }
});

/**
 * Parses a dot path into object keys.
 * @param {string} path Dot path.
 * @returns {(string | number)[]} Parsed key path.
 */
const parsePath = (path) =>
  path.split(".").map((part) => (/^\d+$/.test(part) ? Number(part) : part));

/**
 * Gets a nested value by dot path.
 * @param {{ target: object; path: string }} options Get options.
 * @returns {unknown} Nested value.
 */
const getByPath = ({ target, path }) =>
  parsePath(path).reduce((cursor, key) => cursor?.[key], target);

/**
 * Sets a nested value by dot path.
 * @param {{ target: object; path: string; value: unknown }} options Set options.
 * @returns {void}
 */
const setByPath = ({ target, path, value }) => {
  const keys = parsePath(path);
  const leafKey = keys[keys.length - 1];
  const parent = keys.slice(0, -1).reduce((cursor, key) => cursor[key], target);
  parent[leafKey] = value;
};

/**
 * Formats field error list content.
 * @param {string[]} messages Error messages.
 * @returns {string} HTML list items.
 */
const renderErrorItems = (messages) =>
  messages.map((message) => `<li>${message}</li>`).join("");

/**
 * Normalizes a phone value for payload consistency.
 * @param {{ value: string }} options Phone value options.
 * @returns {string} Normalized phone number string.
 */
const normalizePhoneValue = ({ value }) => {
  let nextValue = String(value ?? "").replace(/\s+/g, "");

  if (!nextValue) return DEFAULT_PHONE_PREFIX;

  if (!nextValue.startsWith("+")) {
    nextValue = `+${nextValue}`;
  }

  const digitsOnly = nextValue.slice(1).replace(/\+/g, "").replace(/[^0-9]/g, "");
  if (!digitsOnly) return "+";

  if (!digitsOnly.startsWith("44")) {
    return `+${digitsOnly}`;
  }

  let nationalPart = digitsOnly.slice(2);
  if (nationalPart.startsWith("0")) {
    nationalPart = nationalPart.slice(1);
  }
  nationalPart = nationalPart.slice(0, UK_E164_NATIONAL_NUMBER_LENGTH);

  return `+44${nationalPart}`;
};

/**
 * Normalizes a VAT value for payload/UI consistency.
 * @param {{ value: string }} options VAT value options.
 * @returns {string} Normalized VAT number string.
 */
const normalizeVatNumberValue = ({ value }) =>
  String(value ?? "").replace(/\s+/g, "").toUpperCase();

if (!customElements.get("registration-form")) {
  customElements.define(
    "registration-form",
    class RegistrationForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.submitButton = this.querySelector("[data-submit-button]");
        this.faciaGroupWrapper = this.querySelector("[data-facia-group-wrapper]");
        this.faciaGroupSelect = this.querySelector('[name="company.faciaGroupId"]');
        this.faciaIdWrapper = this.querySelector("[data-facia-id-wrapper]");
        this.faciaIdInput = this.querySelector('[name="company.faciaId"]');
        this.faciaIdHelp = this.querySelector("[data-facia-id-help]");
        this.legalFieldsWrapper = this.querySelector("[data-legal-fields-wrapper]");
        this.billingSameCheckbox = this.querySelector('[name="company.location.billingSameAsShipping"]');
        this.billingAddressWrapper = this.querySelector("[data-billing-address-wrapper]");
        this.vatNumberInput = this.querySelector('[name="company.vatNumber"]');
        this.globalErrorsWrapper = this.querySelector("[data-global-errors]");
        this.globalErrorsList = this.globalErrorsWrapper?.querySelector("ul");
        this.finishedStateWrapper = this.querySelector("[data-finished-state]");
        this.finishedStateTitle = this.querySelector("[data-finished-title]");
        this.finishedStateDescription = this.querySelector("[data-finished-description]");
        this.finishedPartialWrapper = this.querySelector("[data-finished-partial-errors]");
        this.finishedPartialList = this.finishedPartialWrapper?.querySelector("ul");
        this.endpoint = this.dataset.registerEndpoint || "http://localhost/register";
        this.storefrontLoginUrl = this.dataset.storefrontLoginUrl || "";
        this.postAuthReturnTo = this.dataset.postAuthReturnTo || "/";
        this.captchaKey = this.dataset.captchaKey || "";
        this.recaptchaReadyPromise = null;
        this.recaptchaWidgetId = null;
        this.recaptchaPendingToken = null;
        this.latestSubmittedEmail = "";

        this.state = {
          form: createInitialForm(),
          fieldErrors: {},
          globalErrors: [],
          isSubmitting: false,
          isFinished: false,
          isLocked: false
        };

        this.onInput = this.onInput.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onFocusIn = this.onFocusIn.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
      }

      connectedCallback() {
        if (!this.form || !this.submitButton) return;

        this.syncStaticInputsFromState();
        this.renderConditionalSections();
        this.ensureRecaptchaContainer();
        this.preloadRecaptcha();

        this.addEventListener("input", this.onInput);
        this.addEventListener("change", this.onChange);
        this.addEventListener("click", this.onClick);
        this.addEventListener("focusin", this.onFocusIn);
        this.form.addEventListener("submit", this.onSubmit);
      }

      disconnectedCallback() {
        if (!this.form) return;
        this.removeEventListener("input", this.onInput);
        this.removeEventListener("change", this.onChange);
        this.removeEventListener("click", this.onClick);
        this.removeEventListener("focusin", this.onFocusIn);
        this.form.removeEventListener("submit", this.onSubmit);
        this.clearPendingRecaptchaToken({
          error: new Error("Captcha verification was interrupted.")
        });
      }

      /**
       * Preloads reCAPTCHA script and widget on page load.
       * @returns {void}
       */
      preloadRecaptcha() {
        if (!this.captchaKey) return;

        Promise.resolve()
          .then(() => this.loadRecaptchaApi())
          .then(() => this.ensureRecaptchaWidget())
          .catch(() => {
            // Keep silent here; submit flow surfaces user-facing errors when needed.
          });
      }

      /**
       * Ensures an element exists for invisible reCAPTCHA rendering.
       * @returns {HTMLDivElement | null} Captcha container.
       */
      ensureRecaptchaContainer() {
        if (!this.form) return null;
        const existing = this.querySelector("[data-recaptcha-container]");
        if (existing instanceof HTMLDivElement) return existing;

        const container = document.createElement("div");
        container.dataset.recaptchaContainer = "true";
        this.form.append(container);
        return container;
      }

      /**
       * Clears any pending reCAPTCHA token promise.
       * @param {{ error: Error }} options Clear options.
       * @returns {void}
       */
      clearPendingRecaptchaToken({ error }) {
        if (!this.recaptchaPendingToken) return;
        clearTimeout(this.recaptchaPendingToken.timeoutId);
        this.recaptchaPendingToken.reject(error);
        this.recaptchaPendingToken = null;
      }

      /**
       * Handles successful invisible reCAPTCHA execution.
       * @param {string} token Captcha token.
       * @returns {void}
       */
      onRecaptchaSuccess(token) {
        if (!this.recaptchaPendingToken) return;
        clearTimeout(this.recaptchaPendingToken.timeoutId);
        this.recaptchaPendingToken.resolve(token);
        this.recaptchaPendingToken = null;
      }

      /**
       * Handles invisible reCAPTCHA failure/expiry callbacks.
       * @returns {void}
       */
      onRecaptchaFailure() {
        this.clearPendingRecaptchaToken({
          error: new Error("Captcha check failed. Please try again.")
        });
      }

      /**
       * Loads and waits for reCAPTCHA v2 readiness.
       * @returns {Promise<ReCaptchaV2.ReCaptcha>} reCAPTCHA API object.
       */
      loadRecaptchaApi() {
        if (this.recaptchaReadyPromise) return this.recaptchaReadyPromise;

        if (!this.captchaKey) {
          this.recaptchaReadyPromise = Promise.reject(new Error("Missing reCAPTCHA site key."));
          return this.recaptchaReadyPromise;
        }

        this.recaptchaReadyPromise = new Promise((resolve, reject) => {
          const resolveWhenRecaptchaReady = () => {
            const startedAt = Date.now();

            const pollUntilAvailable = () => {
              if (window.grecaptcha?.render) {
                if (typeof window.grecaptcha.ready === "function") {
                  window.grecaptcha.ready(() => resolve(window.grecaptcha));
                } else {
                  resolve(window.grecaptcha);
                }
                return;
              }

              if (Date.now() - startedAt >= 4000) {
                reject(new Error("reCAPTCHA is unavailable."));
                return;
              }

              window.setTimeout(pollUntilAvailable, 50);
            };

            pollUntilAvailable();
          };

          const onReady = () => {
            resolveWhenRecaptchaReady();
          };

          const onError = () => {
            reject(new Error("Unable to load reCAPTCHA. Please refresh and try again."));
          };

          const existingScript = document.querySelector(RECAPTCHA_SCRIPT_SELECTOR);
          if (existingScript instanceof HTMLScriptElement) {
            if (window.grecaptcha?.render) {
              onReady();
              return;
            }
            existingScript.addEventListener("load", onReady, { once: true });
            existingScript.addEventListener("error", onError, { once: true });
            return;
          }

          const script = document.createElement("script");
          script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
          script.async = true;
          script.defer = true;
          script.dataset.recaptchaV2 = "registration-form";
          script.addEventListener("load", onReady, { once: true });
          script.addEventListener("error", onError, { once: true });
          document.head.append(script);
        });

        return this.recaptchaReadyPromise;
      }

      /**
       * Renders the invisible captcha widget once.
       * @returns {Promise<number>} reCAPTCHA widget id.
       */
      async ensureRecaptchaWidget() {
        if (typeof this.recaptchaWidgetId === "number") {
          return this.recaptchaWidgetId;
        }

        const container = this.ensureRecaptchaContainer();
        if (!container) {
          throw new Error("Unable to initialize captcha container.");
        }

        const recaptcha = await this.loadRecaptchaApi();
        this.recaptchaWidgetId = recaptcha.render(container, {
          sitekey: this.captchaKey,
          size: "invisible",
          callback: (token) => this.onRecaptchaSuccess(token),
          "error-callback": () => this.onRecaptchaFailure(),
          "expired-callback": () => this.onRecaptchaFailure()
        });

        return this.recaptchaWidgetId;
      }

      /**
       * Generates a reCAPTCHA token for registration submit.
       * @returns {Promise<string>} reCAPTCHA v3 token.
       */
      async getRecaptchaToken() {
        const recaptcha = await this.loadRecaptchaApi();
        const widgetId = await this.ensureRecaptchaWidget();

        if (typeof recaptcha.reset === "function") {
          recaptcha.reset(widgetId);
        }
        this.clearPendingRecaptchaToken({
          error: new Error("Captcha check was replaced by a newer request.")
        });

        return new Promise((resolve, reject) => {
          const timeoutId = window.setTimeout(() => {
            this.onRecaptchaFailure();
          }, RECAPTCHA_TOKEN_TIMEOUT_MS);

          this.recaptchaPendingToken = { resolve, reject, timeoutId };

          try {
            recaptcha.execute(widgetId);
          } catch {
            this.onRecaptchaFailure();
          }
        });
      }

      /**
       * Handles free text or select input updates.
       * @param {Event} event DOM input event.
       * @returns {void}
       */
      onInput(event) {
        if (this.state.isLocked) return;
        const target = event.target;
        if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement)) return;
        if (!target.name) return;
        this.applyInputValue({ target });
      }

      /**
       * Forces caret behavior for phone inputs on focus.
       * @param {FocusEvent} event DOM focus event.
       * @returns {void}
       */
      onFocusIn(event) {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (target.name !== "company.user.phone") return;
        this.moveCaretToEnd({ input: target });
      }

      /**
       * Handles checkbox/radio change updates.
       * @param {Event} event DOM change event.
       * @returns {void}
       */
      onChange(event) {
        if (this.state.isLocked) return;
        const target = event.target;
        if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement)) return;

        if (!target.name) return;
        this.applyInputValue({ target });
        this.renderConditionalSections();
      }

      /**
       * Handles click interactions.
       * @param {Event} event DOM click event.
       * @returns {void}
       */
      onClick(event) {
        if (this.state.isLocked) return;
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (target.name === "company.user.phone") {
          this.moveCaretToEnd({ input: target });
        }
      }

      /**
       * Handles form submit.
       * @param {SubmitEvent} event Submit event.
       * @returns {Promise<void>}
       */
      async onSubmit(event) {
        event.preventDefault();
        if (this.state.isLocked || this.state.isSubmitting || this.state.isFinished) return;

        this.clearErrors();

        const payload = this.getPayloadForValidation();
        const parsed = RegistrationFormSchema.safeParse(payload);
        if (!parsed.success) {
          this.applyZodIssues(parsed.error.issues);
          return;
        }
        this.latestSubmittedEmail = parsed.data.company.user.email;

        const shopId = this.dataset.shopId || "";
        if (!shopId) {
          this.state.globalErrors = ['Missing required "shopId" for registration.'];
          this.renderGlobalErrors();
          return;
        }

        this.state.isSubmitting = true;
        this.submitButton.disabled = true;
        this.submitButton.textContent = "Submitting...";

        try {
          const recaptchaToken = await this.getRecaptchaToken();

          const endpoint = new URL(this.endpoint);
          endpoint.searchParams.set("shopId", shopId);

          const response = await fetch(endpoint.toString(), {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              ...payload,
              captcha: {
                token: recaptchaToken,
                action: RECAPTCHA_ACTION
              }
            })
          });

          const body = await response.json().catch(() => ({}));
          if (!response.ok || !body.success) {
            this.applyServerErrors({ status: response.status, body });
            return;
          }

          if (body.partialErrors) {
            this.finish({
              isPartial: true,
              partialErrors: body.partialErrors
            });
            return;
          }

          this.finish({ isPartial: false });
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? `Submission failed: ${error.message}`
              : "Submission failed due to an unknown network error.";
          this.state.globalErrors = [
            errorMessage
          ];
          this.renderGlobalErrors();
          if (!this.isCaptchaError({ message: errorMessage })) {
            this.scrollToTopOfForm();
          }
        } finally {
          if (!this.state.isFinished) {
            this.state.isSubmitting = false;
            this.submitButton.disabled = false;
            this.submitButton.textContent = "Submit";
          }
        }
      }

      /**
       * Applies a single form control value to state.
       * @param {{ target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement }} options Apply options.
       * @returns {void}
       */
      applyInputValue({ target }) {
        let nextValue = target instanceof HTMLInputElement && target.type === "checkbox" ? target.checked : target.value;

        if (
          typeof nextValue === "string" &&
          target.name === "company.user.phone"
        ) {
          nextValue = normalizePhoneValue({ value: nextValue });
          target.value = nextValue;
        }

        if (
          typeof nextValue === "string" &&
          target.name === "company.vatNumber"
        ) {
          nextValue = normalizeVatNumberValue({ value: nextValue });
          target.value = nextValue;
        }

        setByPath({
          target: this.state.form,
          path: target.name,
          value: nextValue
        });

        if (
          target instanceof HTMLInputElement &&
          target.name === "company.user.phone" &&
          typeof nextValue === "string"
        ) {
          this.applyPhoneFieldConstraints({ input: target, value: nextValue });
        }

        if (
          target instanceof HTMLInputElement &&
          target.name === "company.vatNumber" &&
          typeof nextValue === "string"
        ) {
          this.applyVatFieldConstraints({ input: target });
        }

        this.clearErrors();
      }

      /**
       * Builds payload respecting billingSameAsShipping rule.
       * @returns {import("../../../apps/gadget/shared/types/RegistrationForm.ts").RegistrationForm} Payload.
       */
      getPayloadForValidation() {
        const form = structuredClone(this.state.form);
        form.company.user.phone = normalizePhoneValue({ value: form.company.user.phone });

        if (form.company.legalStatus === "SOLE_TRADER") {
          form.company.vatNumber = undefined;
          form.company.companyRegistrationNumber = undefined;
        }
        if (form.company.location.billingSameAsShipping) {
          form.company.location.billingAddress = undefined;
        }
        if (form.company.tradeRelationship !== "FACIA_GROUP") {
          form.company.faciaGroupId = "";
          form.company.faciaId = "";
        }
        return form;
      }

      /**
       * Synchronizes static field values from state.
       * @returns {void}
       */
      syncStaticInputsFromState() {
        this.querySelectorAll("[name]").forEach((element) => {
          if (!(element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement)) return;

          const stateValue = getByPath({ target: this.state.form, path: element.name });
          if (element instanceof HTMLInputElement && element.type === "checkbox") {
            element.checked = Boolean(stateValue);
          } else if (element instanceof HTMLInputElement && element.type === "radio") {
            element.checked = String(stateValue) === element.value;
          } else if (typeof stateValue !== "undefined" && stateValue !== null) {
            element.value = String(stateValue);
          } else {
            element.value = "";
          }

          element.disabled = this.state.isLocked;

          if (
            element instanceof HTMLInputElement &&
            element.name === "company.user.phone" &&
            typeof element.value === "string"
          ) {
            this.applyPhoneFieldConstraints({ input: element, value: element.value });
          }

          if (
            element instanceof HTMLInputElement &&
            element.name === "company.vatNumber"
          ) {
            element.value = normalizeVatNumberValue({ value: element.value });
            this.applyVatFieldConstraints({ input: element });
          }
        });
      }

      /**
       * Renders conditional UI elements.
       * @returns {void}
       */
      renderConditionalSections() {
        if (this.legalFieldsWrapper) {
          const shouldShowLegalFields = ["LIMITED_COMPANY", "PARTNERSHIP"].includes(this.state.form.company.legalStatus);
          this.legalFieldsWrapper.hidden = !shouldShowLegalFields;
          this.legalFieldsWrapper.style.display = shouldShowLegalFields ? "grid" : "none";

          if (!shouldShowLegalFields) {
            this.state.form.company.vatNumber = "";
            this.state.form.company.companyRegistrationNumber = "";
            const vatInput = this.querySelector('[name="company.vatNumber"]');
            const crnInput = this.querySelector('[name="company.companyRegistrationNumber"]');
            if (vatInput instanceof HTMLInputElement) vatInput.value = "";
            if (crnInput instanceof HTMLInputElement) crnInput.value = "";
          }
        }

        if (this.faciaGroupWrapper) {
          const shouldShow = this.state.form.company.tradeRelationship === "FACIA_GROUP";
          this.faciaGroupWrapper.hidden = !shouldShow;
          this.faciaGroupWrapper.style.display = shouldShow ? "flex" : "none";
          if (!shouldShow && this.faciaGroupSelect) this.faciaGroupSelect.value = "";
        }

        if (this.faciaIdWrapper) {
          const shouldShow = this.state.form.company.tradeRelationship === "FACIA_GROUP";
          this.faciaIdWrapper.hidden = !shouldShow;
          this.faciaIdWrapper.style.display = shouldShow ? "flex" : "none";

          if (!shouldShow) {
            this.state.form.company.faciaId = "";
            if (this.faciaIdInput) this.faciaIdInput.value = "";
          }

          this.renderFaciaIdHelp();
        }

        if (this.billingAddressWrapper) {
          const shouldShowBilling = !this.state.form.company.location.billingSameAsShipping;
          this.billingAddressWrapper.hidden = !shouldShowBilling;
          this.billingAddressWrapper.style.display = shouldShowBilling ? "flex" : "none";
        }
      }

      /**
       * Renders contextual helper text for faciaId based on selected facia group.
       * @returns {void}
       */
      renderFaciaIdHelp() {
        if (!this.faciaIdHelp || !this.faciaGroupSelect) return;

        const selectedOption =
          this.faciaGroupSelect.options[this.faciaGroupSelect.selectedIndex] ?? null;
        const displayName = selectedOption?.dataset?.displayName || selectedOption?.textContent?.trim() || "";

        if (!displayName || !selectedOption?.value) {
          this.faciaIdHelp.textContent = "";
          return;
        }

        this.faciaIdHelp.textContent = `The identifier that ${displayName} has provided for your business. This may be your account number or another unique identifier.`;
      }

      /**
       * Applies phone-specific constraints like UK max length.
       * @param {{ input: HTMLInputElement; value: string }} options Field options.
       * @returns {void}
       */
      applyPhoneFieldConstraints({ input, value }) {
        const isUkValue = String(value).startsWith(DEFAULT_PHONE_PREFIX);
        if (isUkValue) {
          input.maxLength = DEFAULT_PHONE_PREFIX.length + UK_E164_NATIONAL_NUMBER_LENGTH;
          return;
        }
        input.removeAttribute("maxlength");
      }

      /**
       * Applies VAT-specific constraints.
       * @param {{ input: HTMLInputElement }} options Field options.
       * @returns {void}
       */
      applyVatFieldConstraints({ input }) {
        input.maxLength = VAT_MAX_LENGTH;
      }

      /**
       * Moves phone field caret to end and clears selection.
       * @param {{ input: HTMLInputElement }} options Input options.
       * @returns {void}
       */
      moveCaretToEnd({ input }) {
        const applyCaret = () => {
          const length = input.value.length;
          input.setSelectionRange(length, length);
        };
        requestAnimationFrame(applyCaret);
      }

      /**
       * Applies zod validation issues to field/global buckets.
       * @param {{ path: (string | number)[]; message: string }[]} issues Validation issues.
       * @returns {void}
       */
      applyZodIssues(issues) {
        const fieldErrors = {};
        const globalErrors = [];

        issues.forEach((issue) => {
          const key = issue.path.join(".");
          if (!key) {
            globalErrors.push(issue.message);
            return;
          }
          fieldErrors[key] = [...(fieldErrors[key] ?? []), issue.message];
        });

        this.state.fieldErrors = fieldErrors;
        this.state.globalErrors = globalErrors;
        this.renderFieldErrors();
        this.renderGlobalErrors();
      }

      /**
       * Applies backend response errors.
       * @param {{ status: number; body: any }} options Response details.
       * @returns {void}
       */
      applyServerErrors({ status, body }) {
        if (Array.isArray(body?.issues)) {
          this.applyZodIssues(body.issues);
          return;
        }

        const messageFromData = Array.isArray(body?.data)
          ? body.data.find((entry) => typeof entry?.message === "string")?.message
          : "";
        const message = messageFromData || body?.error || `Registration failed with status ${status}.`;
        this.state.globalErrors = [message];
        this.renderGlobalErrors();
        if (!this.isCaptchaError({ status, message })) {
          this.scrollToTopOfForm();
        }
      }

      /**
       * Determines whether an error is captcha-related.
       * @param {{ status?: number; message?: string }} options Error context.
       * @returns {boolean} True when error appears captcha-related.
       */
      isCaptchaError({ status, message = "" }) {
        if (status === 403) return true;
        return /captcha/i.test(message);
      }

      /**
       * Scrolls the user back to the top of the form.
       * @returns {void}
       */
      scrollToTopOfForm() {
        const target = this.form || this;
        target.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      }

      /**
       * Renders field-level error lists.
       * @returns {void}
       */
      renderFieldErrors() {
        this.querySelectorAll("[data-error-for]").forEach((list) => {
          if (!(list instanceof HTMLUListElement)) return;
          const path = list.dataset.errorFor;
          if (!path) return;
          const messages = this.state.fieldErrors[path] ?? [];
          list.innerHTML = renderErrorItems(messages);
          list.hidden = messages.length === 0;
        });
      }

      /**
       * Renders global error list.
       * @returns {void}
       */
      renderGlobalErrors() {
        if (!this.globalErrorsWrapper || !this.globalErrorsList) return;
        this.globalErrorsList.innerHTML = renderErrorItems(this.state.globalErrors);
        this.globalErrorsWrapper.hidden = this.state.globalErrors.length === 0;
      }

      /**
       * Clears all errors.
       * @returns {void}
       */
      clearErrors() {
        this.state.fieldErrors = {};
        this.state.globalErrors = [];
        this.renderFieldErrors();
        this.renderGlobalErrors();
      }

      /**
       * Moves form into completed state.
       * @param {{ isPartial: boolean; partialErrors?: any }} options Finish options.
       * @returns {void}
       */
      finish({ isPartial, partialErrors = null }) {
        this.state.isFinished = true;
        this.state.isLocked = true;
        this.state.isSubmitting = false;

        this.lockForm();

        if (!this.finishedStateWrapper || !this.finishedStateTitle || !this.finishedStateDescription) return;

        this.finishedStateWrapper.hidden = false;
        this.finishedStateTitle.textContent = isPartial
          ? "Registration finished with follow-up issues"
          : "Registration complete";
        this.finishedStateDescription.textContent = isPartial
          ? "Your company was created, but some follow-up steps failed. This submission is now locked."
          : "Your company registration was submitted successfully.";

        if (this.finishedPartialWrapper && this.finishedPartialList) {
          if (isPartial && partialErrors) {
            this.finishedPartialWrapper.hidden = false;
            this.finishedPartialList.innerHTML = this.formatPartialErrors({ partialErrors });
          } else {
            this.finishedPartialWrapper.hidden = true;
            this.finishedPartialList.innerHTML = "";
          }
        }

        this.submitButton.disabled = true;
        this.submitButton.textContent = isPartial ? "Finished with issues" : "Finished";

        if (!isPartial) {
          this.redirectToLoginWithHint();
        }
      }

      /**
       * Builds the storefront login URL with a login_hint value.
       * @returns {string} Redirect URL string.
       */
      buildLoginHintRedirectUrl() {
        if (!this.storefrontLoginUrl) return "";

        try {
          const url = new URL(this.storefrontLoginUrl, window.location.origin);
          url.searchParams.set("return_to", this.normalizeReturnToPath());

          const email = String(this.latestSubmittedEmail || this.state.form.company.user.email || "").trim();
          if (email) {
            url.searchParams.set("login_hint", email);
          }
          return url.toString();
        } catch {
          return "";
        }
      }

      /**
       * Normalizes configured redirect destination into a relative return_to path.
       * @returns {string} Relative path for return_to.
       */
      normalizeReturnToPath() {
        const configured = String(this.postAuthReturnTo || "").trim();
        if (!configured) return "/";

        if (configured.startsWith("/")) return configured;

        try {
          const url = new URL(configured, window.location.origin);
          if (url.origin !== window.location.origin) {
            return "/";
          }
          return `${url.pathname}${url.search}${url.hash}` || "/";
        } catch {
          return "/";
        }
      }

      /**
       * Redirects to storefront login with the submitted email prefilled.
       * @returns {void}
       */
      redirectToLoginWithHint() {
        const redirectUrl = this.buildLoginHintRedirectUrl();
        if (!redirectUrl) return;
        window.location.assign(redirectUrl);
      }

      /**
       * Converts backend partial errors to list items.
       * @param {{ partialErrors: any }} options Partial errors.
       * @returns {string} HTML list items.
       */
      formatPartialErrors({ partialErrors }) {
        const messages = [];

        if (Array.isArray(partialErrors?.metafields)) {
          partialErrors.metafields.forEach((error) => {
            const field = Array.isArray(error?.field) ? error.field.join(".") : "metafields";
            messages.push(`Metafield update failed (${field}): ${error?.message || "Unknown error"}`);
          });
        }

        if (!messages.length) {
          messages.push("One or more follow-up actions failed.");
        }

        return renderErrorItems(messages);
      }

      /**
       * Disables all form controls to prevent edits/resubmissions.
       * @returns {void}
       */
      lockForm() {
        if (!this.form) return;
        this.form.querySelectorAll("input, select, textarea, button").forEach((element) => {
          if (!(element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement || element instanceof HTMLButtonElement)) return;
          element.disabled = true;
        });
      }
    }
  );
}
